import React, { useEffect, useState, useCallback } from "react";
import { Gift, Users, Copy, Share2, TicketPercent, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * Portefeuille Price — refonte design (Fév 2026).
 *
 * Objectifs :
 *   • épuré, un vrai objet fini (pas un accordéon de sections)
 *   • hiérarchie visuelle claire : solde en héro, progression fidélité
 *     visible sans scroll, parrainage relégué en zone d'action
 *   • conserve strictement les fonctionnalités existantes :
 *     - affichage du solde (non retirable)
 *     - progression fidélité (trajets + spent)
 *     - code + partage parrainage
 *     - saisie code parrainage (avant 1er trajet payé)
 */
export default function PriceWalletCard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyCode, setApplyCode] = useState("");
  const [applyLoading, setApplyLoading] = useState(false);
  const [alreadyReferred, setAlreadyReferred] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/price/wallet");
      setData(data);
      const r = data?.referral || {};
      // Le champ s'ouvre dès que le backend estime qu'AU MOINS un type
      // de code (parrainage OU créateur) reste applicable — c'est lui
      // qui a toute la logique métier.
      setAlreadyReferred(r.can_apply_code === false);
    } catch (_e) {
      // best-effort — on retente au prochain open
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const applyReferralCode = async () => {
    const code = (applyCode || "").trim().toUpperCase();
    if (code.length < 4) {
      toast.error("Code trop court");
      return;
    }
    setApplyLoading(true);
    try {
      const { data: res } = await api.post("/price/wallet/apply-referral", { code });
      toast.success(res?.message || "Code appliqué !");
      setApplyCode("");
      setApplyOpen(false);
      // Recharge pour rafraîchir can_apply_code / soldes. On ne verrouille
      // pas manuellement l'affichage : le backend sait si un autre code
      // (créateur OU parrainage) reste possible.
      load();
    } catch (e) {
      const msg = formatApiError(e);
      if (/déjà un parrain/i.test(msg)) setAlreadyReferred(true);
      toast.error(msg);
    } finally {
      setApplyLoading(false);
    }
  };

  const shareOrCopy = async () => {
    const link = data?.referral?.share_url;
    if (!link) return;
    const text = `Rejoins-moi sur Price ! Covoiturage simple et abordable. Utilise mon code ${data.referral.code} : ${link}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Price Covoiturage", text, url: link });
        return;
      }
      await navigator.clipboard.writeText(text);
      toast.success("Lien copié");
    } catch (_e) { /* user cancelled */ }
  };

  const copyCode = async () => {
    const code = data?.referral?.code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Code ${code} copié`);
    } catch (_e) { /* noop */ }
  };

  if (loading || !data) {
    return (
      <div className="rounded-3xl bg-white p-5 border-2 border-black shadow-[0_3px_0_0_#000]" data-testid="wallet-card-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-3 w-20 rounded bg-black/10" />
          <div className="h-10 w-32 rounded bg-black/10" />
          <div className="h-2 w-full rounded bg-black/10" />
          <div className="h-16 w-full rounded bg-black/10" />
        </div>
      </div>
    );
  }

  const l = data.loyalty || {};
  const r = data.referral || {};
  const trips = Number(l.trips || 0);
  const goalTrips = Number(l.goal_trips || 5);
  const spent = Number(l.spent_eur || 0);
  const goalSpent = Number(l.goal_spent_eur || 50);
  const rewardEur = Number(l.reward_eur || 10);
  const progressPct = Math.min(100, Math.round((trips / goalTrips) * 100));
  const spentPct = Math.min(100, Math.round((spent / goalSpent) * 100));
  const filleuls = Number(r.filleuls_active_count || 0);

  return (
    <div
      data-testid="wallet-card"
      className="rounded-3xl bg-white border-2 border-black shadow-[0_3px_0_0_#000] overflow-hidden"
    >
      {/* ─── HERO — Solde ─────────────────────────────────────── */}
      <div className="px-5 pt-5 pb-4 border-b-2 border-black/10">
        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-foreground/50">
          Cagnotte
        </div>
        <div className="mt-1 flex items-baseline gap-1">
          <span
            className="font-[Outfit] font-black text-[42px] leading-none text-foreground tabular-nums"
            data-testid="wallet-balance"
          >
            {Number(data.balance_eur || 0).toFixed(2).replace(".", ",")}
          </span>
          <span className="font-[Outfit] font-black text-[24px] text-foreground/40">€</span>
        </div>
        <div className="mt-1.5 text-[11px] font-medium text-foreground/50">
          Utilisable sur tes prochains trajets · Non retirable
        </div>
      </div>

      {/* ─── FIDÉLITÉ ────────────────────────────────────────── */}
      <div className="px-5 pt-4 pb-4 border-b-2 border-black/10">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-foreground/50">
            Carte fidélité
          </div>
          <div className="flex items-center gap-1 text-[11px] font-black text-primary">
            <span className="tabular-nums">{trips}</span>
            <span className="text-foreground/40">/</span>
            <span className="tabular-nums">{goalTrips}</span>
            <span className="ml-0.5 text-foreground/60 font-bold">trajets</span>
          </div>
        </div>

        {/* Barre progression trajets — 5 segments visuels épurés */}
        <div className="flex gap-1.5" data-testid="wallet-loyalty-progress">
          {Array.from({ length: goalTrips }).map((_, i) => {
            const filled = i < trips;
            return (
              <div
                key={i}
                className={
                  "flex-1 h-2 rounded-full transition-all duration-500 " +
                  (filled ? "bg-primary" : "bg-black/8")
                }
              />
            );
          })}
        </div>

        {/* Ligne récap dépense + récompense */}
        <div className="mt-3 flex items-center justify-between">
          <div className="text-[11px] font-medium text-foreground/60 tabular-nums">
            {spent.toFixed(2).replace(".", ",")} €
            <span className="text-foreground/40"> / {goalSpent.toFixed(0)} €</span>
          </div>
          <div className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-black text-primary">
            <Gift className="h-3 w-3" strokeWidth={2.75} />
            +{rewardEur.toFixed(0)} € à débloquer
          </div>
        </div>

        {/* Sous-barre spent (fine) */}
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-black/6">
          <div
            className="h-full rounded-full bg-primary/60 transition-all duration-500"
            style={{ width: `${spentPct}%` }}
          />
        </div>

        {l.cycles_completed > 0 && (
          <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-700">
            <Check className="h-3 w-3" strokeWidth={3} />
            {l.cycles_completed} cycle{l.cycles_completed > 1 ? "s" : ""} bouclé{l.cycles_completed > 1 ? "s" : ""}
          </div>
        )}
      </div>

      {/* ─── PARRAINAGE ──────────────────────────────────────── */}
      <div className="px-5 pt-4 pb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-foreground/50">
            Parrainage
          </div>
          {filleuls > 0 && (
            <div className="inline-flex items-center gap-1 text-[11px] font-black text-emerald-700">
              <Users className="h-3 w-3" strokeWidth={2.75} />
              {filleuls} actif{filleuls > 1 ? "s" : ""}
            </div>
          )}
        </div>

        <div className="text-[12px] font-medium text-foreground/70 leading-relaxed">
          Invite tes proches, ils covoiturent.{" "}
          <span className="font-black text-foreground">
            {Number(r.bonus_eur || 1).toFixed(0)} € pour chacun.
          </span>
        </div>

        {r.code && (
          <>
            <div className="mt-3 flex items-stretch gap-2">
              <button
                type="button"
                onClick={copyCode}
                data-testid="wallet-copy-code"
                className="flex-1 min-w-0 rounded-xl bg-black/5 border-2 border-transparent hover:border-black/20 px-3 py-2.5 flex items-center gap-2 active:translate-y-[1px] transition-all"
              >
                <span className="text-[9px] font-black uppercase tracking-widest text-foreground/50">Code</span>
                <span className="font-[Outfit] font-black text-[14px] tracking-[0.15em] truncate">
                  {r.code}
                </span>
                <Copy className="ml-auto h-3.5 w-3.5 flex-none text-foreground/40" strokeWidth={2.5} />
              </button>
              <button
                type="button"
                onClick={shareOrCopy}
                data-testid="wallet-share"
                className="flex-none rounded-xl bg-primary text-primary-foreground border-2 border-black px-4 py-2.5 flex items-center gap-1.5 active:translate-y-[1px] shadow-[0_2px_0_0_#000]"
              >
                <Share2 className="h-4 w-4" strokeWidth={2.75} />
                <span className="text-[12px] font-black">Partager</span>
              </button>
            </div>

            {(r.filleuls_total > 0 || Number(r.total_referral_earned_eur) > 0) && (
              <div className="mt-3 flex items-center justify-between gap-3 text-[10px] font-bold text-foreground/50 tabular-nums">
                <span>
                  {r.filleuls_total} inscrit{r.filleuls_total > 1 ? "s" : ""}
                </span>
                <span className="h-px flex-1 bg-black/8" />
                <span>
                  Gagné : {Number(r.total_referral_earned_eur || 0).toFixed(2).replace(".", ",")} €
                </span>
              </div>
            )}
          </>
        )}

        {!alreadyReferred && (
          <div className="mt-4 pt-4 border-t border-black/8">
            {!applyOpen ? (
              <button
                type="button"
                onClick={() => setApplyOpen(true)}
                data-testid="wallet-apply-code-open"
                className="w-full rounded-xl bg-white border-2 border-dashed border-black/25 px-3 py-2.5 flex items-center justify-center gap-2 active:translate-y-[1px] hover:border-primary transition-colors"
              >
                <TicketPercent className="h-4 w-4 text-primary" strokeWidth={2.5} />
                <span className="text-[12px] font-black text-foreground/80">J'ai un code (parrainage / créateur)</span>
                <ArrowRight className="h-3.5 w-3.5 text-foreground/40" strokeWidth={2.5} />
              </button>
            ) : (
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-foreground/50 mb-1.5">
                  Code parrainage ou créateur
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={applyCode}
                    onChange={(e) => setApplyCode(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 20))}
                    placeholder="JEANMARC ou AB4DXG"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    data-testid="wallet-apply-code-input"
                    className="flex-1 min-w-0 rounded-xl bg-black/5 border-2 border-black/10 px-3 py-2.5 font-[Outfit] font-black tracking-[0.15em] uppercase outline-none focus:bg-white focus:border-black transition-colors"
                    style={{ fontSize: "16px" }}
                  />
                  <button
                    type="button"
                    onClick={applyReferralCode}
                    disabled={applyLoading || applyCode.length < 4}
                    data-testid="wallet-apply-code-submit"
                    className="flex-none rounded-xl bg-primary text-primary-foreground border-2 border-black px-4 py-2.5 text-[12px] font-black active:translate-y-[1px] disabled:opacity-40 shadow-[0_2px_0_0_#000]"
                  >
                    {applyLoading ? "..." : "Valider"}
                  </button>
                </div>
                <div className="mt-1.5 text-[10px] font-medium text-foreground/50">
                  Code parrainage : jusqu'à ton 1er trajet payé. Code créateur : possible à tout moment.
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
